import { useGetAiWorkersByCategoryQuery, useTypedSelector, categoryType, useDeleteAiWorkersMutation, useDeleteVoiceAgentMutation, RouteState, useEntityResourcePermissions } from '@circuitry-ai/doc-data';
import {
    CardListViewMode,
    CardUI,
    checkType,
    CtyButton,
    CtyShowToast,
    EntityCardWithActions,
    EntityListView,
    ListActionsCellWithDelete,
    getActionsColumnDef,
    getDateColumnDef,
    getDescriptionColumnDef,
    getNameColumnDef,
} from '@circuitry-ai/doc-ui';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AgentViewTabs } from './agentview';

function getVoiceAgentIdFromAgent(agent: { aiworkers_attrs?: Array<{ attr_name?: string; attr_value?: string }> } | undefined): string | null {
    const id = agent?.aiworkers_attrs?.find((a) => a?.attr_name === 'voice_agent_id')?.attr_value;
    return id?.trim() ? id.trim() : null;
}

function useDeleteAgentHandler(agent: any, refetchAgentList: () => void) {
    const { t } = useTranslation();
    const [deleteAiWorkers, { isLoading: deleteAgentLoading }] = useDeleteAiWorkersMutation();
    const [deleteVoiceAgent, { isLoading: deleteVoiceAgentLoading }] = useDeleteVoiceAgentMutation();

    const isVoiceAgentSubType = String(agent?.sub_type ?? '').trim().toLowerCase() === 'voice agent';
    const voiceAgentAssistantId = getVoiceAgentIdFromAgent(agent);

    const handleConfirmDelete = useCallback(async (closeModal: () => void) => {
        try {
            if (isVoiceAgentSubType) {
                if (!voiceAgentAssistantId) {
                    CtyShowToast(checkType.error, t('Voice agent is missing linked assistant id.'));
                    return;
                }
                await deleteVoiceAgent(voiceAgentAssistantId).unwrap();
            }
            const response = await deleteAiWorkers(agent?.aiworker_id);
            CtyShowToast(checkType.success, response?.data);
            closeModal();
            refetchAgentList();
        } catch (error) {
            CtyShowToast(checkType.error, (error as Error)?.message ?? 'Delete Operation failed');
        }
    }, [agent?.aiworker_id, deleteAiWorkers, deleteVoiceAgent, isVoiceAgentSubType, refetchAgentList, t, voiceAgentAssistantId]);

    return { handleConfirmDelete, isDeleteLoading: deleteAgentLoading || deleteVoiceAgentLoading };
}

const AgentActionButtonsCell = ({
    agent,
    onNavigate,
    refetchAgentList,
}: {
    readonly agent: any;
    readonly onNavigate: (agent: any, tab: string) => void;
    readonly refetchAgentList: () => void;
}) => {
    const { t } = useTranslation();
    const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('agent', agent?.aiworker_id);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteAgentHandler(agent, refetchAgentList);

    return (
        <ListActionsCellWithDelete
            actions={[
                {
                    label: t('Edit'),
                    onPress: () => onNavigate(agent, AgentViewTabs.DEFINITION),
                    variant: 'bordered',
                    color: 'default',
                    isDisabled: !canUpdate,
                },
                {
                    label: t('Execute'),
                    onPress: () => onNavigate(agent, AgentViewTabs.EXECUTE),
                    variant: 'solid',
                    color: 'primary',
                    isDisabled: !canRead,
                },
            ]}
            canDelete={canDelete}
            entityName={agent?.name ?? ''}
            onConfirmDelete={handleConfirmDelete}
            isDeleteLoading={isDeleteLoading}
            deleteConfirmLabel={t('Delete Agent')}
        />
    );
};

const AgentCardItem = ({ agent, socketUrl, refetchAgentList }: { readonly agent: any; readonly socketUrl: string; readonly refetchAgentList: () => void }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { canRead, canUpdate, canDelete } = useEntityResourcePermissions('agent', agent?.aiworker_id);
    const { handleConfirmDelete, isDeleteLoading } = useDeleteAgentHandler(agent, refetchAgentList);

    const handleNavigation = (tab: string) => {
        const agentData = { ...agent, socketUrl };
        navigate(`/agents/${RouteState.UPDATE}/${agentData.aiworker_id}?tab=${tab}`, { state: { agent: agentData } });
    };

    const footerItems = (
        <>
            <CtyButton onPress={() => handleNavigation('definition')} className={'w-full border-1'} variant={'bordered'} radius='sm' size={'lg'} color={'default'} isDisabled={!canUpdate}>
                {t('Edit')}
            </CtyButton>
            <CtyButton className={'w-full border-1'} variant={'solid'} radius='sm' size={'lg'} color={'primary'} isDisabled={!canRead} onPress={() => handleNavigation(AgentViewTabs.EXECUTE)}>
                {t('Execute')}
            </CtyButton>
        </>
    );

    return (
        <EntityCardWithActions
            id={agent.aiworker_id}
            name={agent.name}
            description={agent.description}
            footerItems={footerItems}
            canDelete={canDelete}
            dropdownAriaLabel="Agent Actions"
            deleteModalEntityName={agent?.name ?? ''}
            onConfirmDelete={handleConfirmDelete}
            isDeleteLoading={isDeleteLoading}
            deleteConfirmLabel={t('Delete Agent')}
        />
    );
};

const AgentsCardsList = [
    {
        name: "Automation",
        id: 'automation',
        description: 'Choose the applications and AI abilities you`d like to integrate with your Advisor.',
        path: '/agents/automation',
    }
];

const renderAgentActionsCell = (handleNavigation: (agent: any, tab: string) => void, refetchAgentList: () => void) =>
    function AgentActionsRenderer(params: any) {
        return (
            <AgentActionButtonsCell agent={params?.data} onNavigate={handleNavigation} refetchAgentList={refetchAgentList} />
        );
    };

export function AgentsList() {
    const token = useTypedSelector((state) => state.auth.token);
    const [skip, setSkip] = useState<boolean>(true);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [viewMode, setViewMode] = useState<CardListViewMode>(CardListViewMode.GRID);

    const { data: agentsList, isLoading, isFetching, error, refetch } = useGetAiWorkersByCategoryQuery({ category: categoryType.AGENT }, { skip, refetchOnMountOrArgChange: true });
    const agentsResponse = agentsList as any;
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        if (token && skip) {
            setSkip(false);
        }
    }, [skip, token]);

    const allAgents = useMemo(() => agentsResponse?.agents ?? [], [agentsResponse]);
    const socketUrl = agentsResponse?.agent_websocket_url ?? '';

    const filteredAgents = useMemo(() => {
        if (!searchQuery.trim()) return allAgents;
        const q = searchQuery.toLowerCase();
        return allAgents.filter((agent: any) => agent.name?.toLowerCase().includes(q));
    }, [allAgents, searchQuery]);

    const handleNavigation = useCallback((agent: any, tab: string) => {
        const agentData = { ...agent, socketUrl };
        navigate(`/agents/${RouteState.UPDATE}/${agentData.aiworker_id}?tab=${tab}`, { state: { agent: agentData } });
    }, [navigate, socketUrl]);

    const columnDefs = useMemo(
        () => [
            getNameColumnDef(t('Agent Name')),
            getDescriptionColumnDef(t('Public Description')),
            getDateColumnDef(t('Last Modified')),
            getActionsColumnDef({
                headerName: t('Actions'),
                maxWidth: 280,
                cellRenderer: renderAgentActionsCell(handleNavigation, refetch),
            }),
        ],
        [handleNavigation, t, refetch]
    );

    if (allAgents.length === 0 && !isLoading && !isFetching && !error) {
        return (
            <div className="flex-1 flex items-center justify-center h-full border-2 border-default-300 rounded-lg m-4 bg-default-100">
                <div className="text-2xl text-primary">{t('No Agents found')}</div>
            </div>
        );
    }

    return (
        <EntityListView
            items={filteredAgents}
            isLoading={isLoading || isFetching}
            error={error}
            searchId="agents-search"
            searchPlaceholder={t('Search agents')}
            onSearch={setSearchQuery}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            emptyMessage={t('No Agents found')}
            cardKey={(agent: any) => agent.aiworker_id}
            renderCard={(agent: any) => (
                <AgentCardItem agent={agent} socketUrl={socketUrl} refetchAgentList={refetch} />
            )}
            extraGridChildren={AgentsCardsList.map((agent: any, index: number) => (
                <CardUI
                    key={`${agent.id}-${index}`}
                    view={agent?.canView ?? false}
                    id={agent.id}
                    name={agent.name}
                    description={agent.description}
                    footerItems={
                        <CtyButton
                            className={'w-full border-1'}
                            variant={'solid'}
                            radius="sm"
                            size={'lg'}
                            color={'primary'}
                            onPress={() => navigate(agent.path)}
                        >
                            {t('Launch')}
                        </CtyButton>
                    }
                />
            ))}
            tableId="agents-list-table"
            columnDefs={columnDefs}
        />
    );
}

export default AgentsList;
